import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiArrowRight, HiArrowLeft, HiCheckCircle, HiVideoCamera } from 'react-icons/hi';
import { FaWhatsapp, FaEnvelope } from 'react-icons/fa';

const TeleConsultation = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '', age: '', contactNumber: '', email: '',
    symptoms: [], otherSymptom: '', duration: '',
    diabetes: '', previousSurgery: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const symptomOptions = [
    'Blurred vision', 'Difficulty seeing at night', 'Sensitivity to light',
    'Eye pain', 'Redness', 'Double vision'
  ];

  const handleSymptomToggle = (s) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(s)
        ? prev.symptoms.filter(item => item !== s)
        : [...prev.symptoms, s]
    }));
  };

  const nextStep = () => setStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  if (submitted) {
    return (
      <div className="pt-40 pb-24 px-4 min-h-screen bg-black flex items-center justify-center mesh-gradient">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="gold-card max-w-xl w-full p-8 md:p-12 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--gold-primary)]/10 blur-[50px] -mr-16 -mt-16" />
          <div className="w-20 h-20 bg-[var(--gold-primary)] text-black rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_var(--gold-glow)]">
            <HiCheckCircle size={48} />
          </div>
          <h2 className="text-3xl font-black text-white mb-4">Request <span className="text-gradient">Secured</span></h2>
          <p className="text-slate-400 font-medium mb-10 leading-relaxed italic">
            Your clinical data has been prioritized. A specialist will review your case. Expect a response via your preferred contact method within <span className="text-[var(--gold-primary)] font-black">24–48 hours</span>.
          </p>
          
          <div className="flex flex-col gap-4 mb-10">
            <a href="#!" className="flex items-center justify-center gap-3 p-5 bg-[#25D366] text-white rounded-2xl font-black hover:bg-[#128C7E] transition-all shadow-xl shadow-green-900/20 text-xs uppercase tracking-widest">
              <FaWhatsapp size={20} /> Urgent: WhatsApp Specialist
            </a>
            <a href="#!" className="flex items-center justify-center gap-3 p-5 bg-white/5 text-white border border-white/10 rounded-2xl font-black hover:bg-white/10 transition-all text-xs uppercase tracking-widest">
              <FaEnvelope size={20} /> Official Case Email
            </a>
          </div>

          <div className="p-8 bg-black/40 border border-white/5 rounded-2xl text-left">
            <h4 className="text-[10px] font-black text-[var(--gold-primary)] uppercase tracking-[0.3em] mb-6">Immediate Protocol</h4>
            <ul className="space-y-4">
              {['Minimize all screen exposure', 'Utilize controlled warm compress', 'Suspend contact lens usage'].map((tip, i) => (
                <li key={i} className="flex items-center gap-4 text-sm font-bold text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-[var(--gold-primary)] shadow-[0_0_8px_var(--gold-glow)]" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-40 pb-24 px-4 min-h-screen bg-black mesh-gradient">
      <div className="max-w-3xl mx-auto relative z-10">
        {/* Luxury Header */}
        <div className="mb-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[var(--gold-primary)] text-black rounded-2xl flex items-center justify-center mb-6 shadow-[0_0_30px_var(--gold-glow)] rotate-6">
            <HiVideoCamera size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Tele-<span className="text-gradient">Consultation</span></h1>
          <p className="text-slate-400 font-medium max-w-lg italic">
            "Professional eye care guidance, bridging the clinical distance with digital excellence."
          </p>
        </div>

        {/* Dynamic Step Indicator */}
        <div className="flex justify-between mb-16 relative">
          <div className="absolute top-1/2 left-0 w-full h-px bg-white/10 -translate-y-1/2" />
          {[1, 2, 3].map(s => (
            <div key={s} className="relative z-10 flex flex-col items-center">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black transition-all duration-700 ${
                step === s ? 'bg-[var(--gold-primary)] text-black shadow-[0_0_25px_var(--gold-glow)] scale-110' :
                step > s ? 'bg-[var(--gold-primary)] text-black' : 'bg-black border border-white/10 text-slate-600'
              }`}>
                {step > s ? <HiCheckCircle size={24} /> : `0${s}`}
              </div>
            </div>
          ))}
        </div>

        {/* Luxury Form Container */}
        <motion.div 
          layout
          className="gold-card p-8 md:p-12 overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--gold-primary)]/5 blur-[100px] -mr-32 -mt-32" />
          
          <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}>
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="s1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <h3 className="text-xl font-black text-white flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-[var(--gold-primary)] text-black flex items-center justify-center text-xs">01</span>
                    Patient Credentials
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {[
                      { l: 'Full Name', v: 'fullName', p: 'e.g. Johnathan Doe', t: 'text' },
                      { l: 'Age', v: 'age', p: 'e.g. 24', t: 'number' },
                      { l: 'Contact Number', v: 'contactNumber', p: '+1 (234) 000-000', t: 'tel' },
                      { l: 'Email (Optional)', v: 'email', p: 'john@example.com', t: 'email' }
                    ].map(f => (
                      <div key={f.v} className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{f.l}</label>
                        <input 
                          required={f.v !== 'email'}
                          type={f.t}
                          placeholder={f.p}
                          className="w-full px-5 py-4 bg-black/40 border border-white/10 rounded-xl focus:border-[var(--gold-primary)] focus:bg-black transition-all font-semibold outline-none text-white placeholder:text-slate-700"
                          value={formData[f.v]}
                          onChange={(e) => setFormData({...formData, [f.v]: e.target.value})}
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="s2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-10"
                >
                  <h3 className="text-xl font-black text-white flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-[var(--gold-primary)] text-black flex items-center justify-center text-xs">02</span>
                    Symptom Analysis
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {symptomOptions.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleSymptomToggle(s)}
                        className={`p-5 rounded-2xl border transition-all duration-500 flex items-center gap-4 ${
                          formData.symptoms.includes(s)
                            ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] shadow-[0_0_20px_var(--gold-glow)]'
                            : 'border-white/5 bg-white/5 text-slate-400 hover:border-white/10'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-md border transition-colors ${
                          formData.symptoms.includes(s) ? 'bg-[var(--gold-primary)] border-[var(--gold-primary)]' : 'border-white/20'
                        }`} />
                        <span className="font-extrabold text-sm uppercase tracking-tight">{s}</span>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Observation Period</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {['< 1 Week', '1–4 Weeks', '> 1 Month'].map(dur => (
                        <button
                          key={dur} type="button"
                          onClick={() => setFormData({...formData, duration: dur})}
                          className={`p-4 rounded-xl border font-black transition-all uppercase tracking-tighter text-xs ${
                            formData.duration === dur ? 'bg-[var(--gold-primary)] border-[var(--gold-primary)] text-black shadow-lg shadow-yellow-900/20' : 'bg-black/40 border-white/5 text-slate-500'
                          }`}
                        >
                          {dur}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="s3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-10"
                >
                  <h3 className="text-xl font-black text-white flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-[var(--gold-primary)] text-black flex items-center justify-center text-xs">03</span>
                    Clinical History
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {[
                      { l: 'Diabetes Condition', v: 'diabetes' },
                      { l: 'Optical Surgery History', v: 'previousSurgery' }
                    ].map(q => (
                      <div key={q.v} className="p-8 bg-black/40 rounded-2xl border border-white/5">
                        <p className="text-sm font-black text-slate-300 mb-6 uppercase tracking-tight">{q.l}</p>
                        <div className="flex gap-4">
                          {['Yes', 'No'].map(ans => (
                            <button
                              key={ans} type="button"
                              onClick={() => setFormData({...formData, [q.v]: ans})}
                              className={`flex-1 py-4 rounded-xl font-black transition-all border ${
                                formData[q.v] === ans ? 'bg-[var(--gold-primary)] border-[var(--gold-primary)] text-black' : 'bg-white/5 border-white/10 text-slate-600'
                              }`}
                            >
                              {ans}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-4 mt-12 pt-10 border-t border-white/10">
              {step > 1 && (
                <button type="button" onClick={prevStep} className="px-10 py-4 bg-white/5 text-slate-400 rounded-xl font-black hover:bg-white/10 transition-all flex items-center gap-2 uppercase text-[10px] tracking-widest">
                  <HiArrowLeft /> BACK
                </button>
              )}
              {step < 3 ? (
                <button type="button" onClick={nextStep} className="flex-1 px-10 py-4 bg-[var(--gold-primary)] text-black rounded-xl font-black transition-all flex items-center justify-center gap-3 group uppercase text-[10px] tracking-widest">
                  NEXT PHASE <HiArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </button>
              ) : (
                <button type="submit" className="flex-1 px-10 py-5 bg-[var(--gold-primary)] text-black rounded-2xl font-black text-sm transition-all active:scale-95 uppercase tracking-widest shadow-xl shadow-yellow-900/20">
                  SUBMIT CLINICAL CASE
                </button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default TeleConsultation;
